import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Newspaper,
  CalendarClock,
  Receipt,
  CheckCircle,
  CheckCheck,
  Clock,
  XCircle,
  AlertCircle,
  Video,
  MapPin,
  BookOpen,
  ChevronRight,
  Crown,
  Calendar,
  Wrench,
  FlaskConical,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TWPageHeader } from '../components/ui/TWPageHeader';
import { TWContainer } from '../components/ui/TWContainer';
import { SidebarLayout } from '../components/layout/SidebarLayout';
import { UserAvatar } from '../components/ui/UserAvatar';
import { AnnouncementsCard } from '../components/announcements/AnnouncementsCard';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useLenis } from '../hooks/useLenis';

// Services
import { getUserAppointments } from '../services/appointment/appointmentService';
import { getMyInvoices, Invoice } from '../services/payment/invoiceService';
import {
  getSubscriptionStatus,
  getStatusLabel,
  getStatusColor as getSubStatusColor,
  SubscriptionStatusResponse,
  UserSubscription,
} from '../services/payment/subscriptionService';
import { getUnreadBlogPosts, markAllBlogPostsAsRead, UnreadBlogData } from '../services/blog/blogService';
import { getApiBaseUrl } from '../services/api/apiClient';
import { getImageUrl } from '../utils/imageUtils';
import { Appointment } from '../components/appointments/appointment.types';

// ============================================================================
// Types
// ============================================================================

interface CardDataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// Custom Hook: Dashboard-Daten laden
// ============================================================================

function useDashboardData() {
  const [appointments, setAppointments] = useState<CardDataState<Appointment[]>>({
    data: null, loading: true, error: null,
  });
  const [invoices, setInvoices] = useState<CardDataState<Invoice[]>>({
    data: null, loading: true, error: null,
  });
  const [subscription, setSubscription] = useState<CardDataState<SubscriptionStatusResponse>>({
    data: null, loading: true, error: null,
  });
  const [unreadBlog, setUnreadBlog] = useState<CardDataState<UnreadBlogData>>({
    data: null, loading: true, error: null,
  });

  const loadAll = useCallback(async () => {
    setAppointments(prev => ({ ...prev, loading: true, error: null }));
    setInvoices(prev => ({ ...prev, loading: true, error: null }));
    setSubscription(prev => ({ ...prev, loading: true, error: null }));
    setUnreadBlog(prev => ({ ...prev, loading: true, error: null }));

    const [apptResult, invResult, subResult, unreadResult] = await Promise.allSettled([
      getUserAppointments('upcoming'),
      getMyInvoices(),
      getSubscriptionStatus(),
      getUnreadBlogPosts(),
    ]);

    setAppointments(
      apptResult.status === 'fulfilled'
        ? { data: apptResult.value, loading: false, error: null }
        : { data: null, loading: false, error: 'Termine konnten nicht geladen werden' }
    );
    setInvoices(
      invResult.status === 'fulfilled'
        ? { data: invResult.value, loading: false, error: null }
        : { data: null, loading: false, error: 'Rechnungen konnten nicht geladen werden' }
    );
    setSubscription(
      subResult.status === 'fulfilled'
        ? { data: subResult.value, loading: false, error: null }
        : { data: null, loading: false, error: 'Abonnement-Status konnte nicht geladen werden' }
    );
    setUnreadBlog(
      unreadResult.status === 'fulfilled'
        ? { data: unreadResult.value, loading: false, error: null }
        : { data: null, loading: false, error: null } // Kein Fehler anzeigen, einfach ignorieren
    );
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return { appointments, invoices, subscription, unreadBlog, reload: loadAll };
}

// ============================================================================
// Hilfsfunktionen
// ============================================================================

/** Datum formatieren: "Di, 18.02." */
const formatShortDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });
  const day = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  return `${weekday}, ${day}`;
};

/** Uhrzeit formatieren: "14:00" -> "14:00" (ohne Sekunden) */
const formatTime = (timeStr: string): string => {
  return timeStr.substring(0, 5);
};

/** Betrag formatieren */
const formatAmount = (amount: number, currency = 'EUR'): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

/** Datum formatieren: "12.02.2026" */
const formatFullDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/** Verbleibende Tage berechnen */
const daysUntil = (dateStr: string): number => {
  const target = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
};

/**
 * Rechnungs-Zahlungsstatus basierend auf payment_status aus der Datenbank.
 * Mögliche DB-Werte: 'pending' | 'paid' | 'partially_paid' | 'failed' | 'refunded'
 */
const getPaymentStatusLabel = (paymentStatus: string): string => {
  const map: Record<string, string> = {
    paid: 'Bezahlt',
    pending: 'Nicht bezahlt',
    partially_paid: 'Teilweise bezahlt',
    failed: 'Fehlgeschlagen',
    refunded: 'Erstattet',
  };
  return map[paymentStatus] || paymentStatus;
};

const getPaymentStatusClasses = (paymentStatus: string): string => {
  const map: Record<string, string> = {
    paid: 'text-green-600 dark:text-green-400 bg-status-success/10',
    pending: 'text-red-600 dark:text-red-400 bg-status-error/10',
    partially_paid: 'text-yellow-600 dark:text-yellow-400 bg-yellow-500/10',
    failed: 'text-red-600 dark:text-red-400 bg-status-error/10',
    refunded: 'text-gray-500 dark:text-gray-400 bg-gray-500/10',
  };
  return map[paymentStatus] || 'text-gray-500 bg-gray-500/10';
};

const PaymentStatusIcon: React.FC<{ paymentStatus: string }> = ({ paymentStatus }) => {
  if (paymentStatus === 'paid') return <CheckCircle className="w-3.5 h-3.5" />;
  if (paymentStatus === 'refunded') return <XCircle className="w-3.5 h-3.5" />;
  if (paymentStatus === 'failed') return <AlertCircle className="w-3.5 h-3.5" />;
  return <Clock className="w-3.5 h-3.5" />;
};

/** PDF-URL für eine Rechnung erzeugen (gleiche Logik wie ProfilePage) */
const getInvoicePdfUrl = (pdfUrl?: string): string | null => {
  if (!pdfUrl || !pdfUrl.startsWith('/invoices/')) return null;
  const baseUrl = getApiBaseUrl();
  const dynamicPath = pdfUrl.startsWith('/invoices/file/') ? pdfUrl : pdfUrl.replace(/^\/invoices\//, '/invoices/file/');
  return `${baseUrl}${dynamicPath}`;
};

/** Termin-Status als Tailwind-Klassen */
const getAppointmentStatusClasses = (status: string): string => {
  const map: Record<string, string> = {
    confirmed: 'text-green-600 dark:text-green-400 bg-status-success/10',
    pending: 'text-yellow-600 dark:text-yellow-400 bg-yellow-500/10',
    completed: 'text-gray-500 dark:text-gray-400 bg-gray-500/10',
    cancelled: 'text-status-error dark:text-red-400 bg-status-error/10',
  };
  return map[status] || 'text-gray-500 bg-gray-500/10';
};

const getAppointmentStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    confirmed: 'Bestätigt',
    pending: 'Ausstehend',
    completed: 'Abgeschlossen',
    cancelled: 'Storniert',
  };
  return map[status] || status;
};

/** Prüft ob ein Abo gekündigt ist
 *  - Stripe: status bleibt 'active', cancelAtPeriodEnd = true
 *  - PayPal: status wird 'cancelled'/'canceled'
 */
const isSubscriptionCancelled = (s: UserSubscription): boolean =>
  s.status === 'canceled' || (s.status as string) === 'cancelled' || (s.status === 'active' && s.cancelAtPeriodEnd);

/** Prüft ob ein Abo noch gültig ist (aktiv, Trial oder gekündigt mit laufendem Zeitraum) */
const isSubscriptionValid = (s: UserSubscription): boolean => {
  if (s.status === 'active' || s.status === 'trialing') return true;
  if (isSubscriptionCancelled(s) && s.currentPeriodEnd && new Date(s.currentPeriodEnd) > new Date()) return true;
  return false;
};


/** Abo-Beschreibung basierend auf planSlug */
const getPlanDescription = (planSlug: string): string => {
  if (planSlug.startsWith('fcms-')) return 'FCMS-Abo';
  if (planSlug === 'bitcoin-stacker') return 'Bitcoin Stacker';
  if (planSlug === 'bitcoin-whale') return 'Bitcoin Whale';
  if (planSlug.startsWith('bitcoin')) return 'Bitcoin-Abo';
  return 'Abo';
};

// ============================================================================
// Skeleton-Komponente
// ============================================================================

const CardSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-3 py-2">
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
  </div>
);

// ============================================================================
// Error-Komponente
// ============================================================================

const CardError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="text-center py-4">
    <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    <button
      onClick={onRetry}
      className="text-sm text-brand hover:text-brand-hover mt-2 transition-colors"
    >
      Erneut versuchen
    </button>
  </div>
);

// ============================================================================
// Leere-State-Komponente
// ============================================================================

const CardEmpty: React.FC<{
  icon: React.ReactNode;
  message: string;
  actionLabel?: string;
  actionHref?: string;
}> = ({ icon, message, actionLabel, actionHref }) => (
  <div className="text-center py-4">
    <div className="text-gray-400 dark:text-gray-500 flex justify-center mb-2">{icon}</div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    {actionLabel && actionHref && (
      <Link
        to={actionHref}
        className="text-sm text-brand hover:text-brand-hover mt-1 inline-block no-underline transition-colors"
      >
        {actionLabel}
      </Link>
    )}
  </div>
);

// ============================================================================
// Karten-Wrapper-Komponente
// ============================================================================

const iconColorClasses = {
  orange: 'bg-orange-500/10 text-orange-500',
  teal: 'bg-teal-500/10 text-teal-500',
  green: 'bg-status-success/10 text-status-success',
  blue: 'bg-status-info/10 text-status-info',
  purple: 'bg-purple-500/10 text-purple-500',
};

const DashboardListCard: React.FC<{
  icon: React.ReactNode;
  iconColor: keyof typeof iconColorClasses;
  label: string;
  actions?: Array<{ label: string; href: string }>;
  children: React.ReactNode;
}> = ({ icon, iconColor, label, actions, children }) => (
  <div className="rounded-xl p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 flex flex-col md:h-full">
    {/* Header */}
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColorClasses[iconColor]}`}>
        {icon}
      </div>
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</span>
    </div>

    {/* Content */}
    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden max-h-[340px] md:max-h-[380px] xl:max-h-none scrollbar-styled touch-pan-y">{children}</div>

    {/* Footer Actions */}
    {actions && actions.length > 0 && (
      <div className={`mt-3 flex gap-2 ${actions.length === 1 ? '' : 'flex-wrap'}`}>
        {actions.map((action, i) => (
          <Link
            key={i}
            to={action.href}
            className={`
              py-2 px-4 rounded-lg text-center text-sm font-medium no-underline transition-colors duration-150
              ${actions.length === 1 ? 'flex-1' : 'flex-1 min-w-0'}
              bg-gray-100 dark:bg-white/5
              text-gray-700 dark:text-gray-300
              border border-gray-200 dark:border-white/10
              hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-gray-100
            `}
          >
            {action.label}
          </Link>
        ))}
      </div>
    )}
  </div>
);

// ============================================================================
// Blog-Karte
// ============================================================================

const BlogCard: React.FC<{
  unreadBlog: CardDataState<UnreadBlogData>;
  onRetry: () => void;
}> = ({ unreadBlog, onRetry }) => {
  const [localData, setLocalData] = useState<UnreadBlogData | null>(unreadBlog.data);

  useEffect(() => {
    setLocalData(unreadBlog.data);
  }, [unreadBlog.data]);

  const handleMarkAllRead = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setLocalData({ unreadCount: 0, recentUnread: [] });
    try {
      await markAllBlogPostsAsRead();
    } catch {
      onRetry();
    }
  }, [onRetry]);

  const renderContent = () => {
    if (unreadBlog.loading) return <CardSkeleton />;
    if (unreadBlog.error) return <CardError message={unreadBlog.error} onRetry={onRetry} />;

    if (!localData || localData.unreadCount === 0) {
      return (
        <CardEmpty
          icon={<BookOpen className="w-8 h-8" />}
          message="Alle Beiträge gelesen"
        />
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {localData.unreadCount} ungelesene{' '}
            {localData.unreadCount === 1 ? 'Beitrag' : 'Beiträge'}
          </p>
          <button
            type="button"
            onClick={handleMarkAllRead}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand transition-colors flex-shrink-0"
            title="Alle als gelesen markieren"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Alle gelesen</span>
          </button>
        </div>

        <div className="space-y-2">
          {localData.recentUnread.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="flex items-start gap-3 p-2 -mx-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-brand dark:hover:text-brand no-underline transition-colors group"
            >
              <div className="relative w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                {post.featuredImage ? (
                  <img
                    src={getImageUrl(post.featuredImage)}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <span className="block line-clamp-2 leading-snug [overflow-wrap:anywhere] hyphens-auto">
                    {post.title}
                  </span>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand/15 text-brand flex-shrink-0 mt-0.5">
                  Neu
                </span>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-3.5" />
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardListCard
      icon={<Newspaper className="w-5 h-5" />}
      iconColor="blue"
      label="Blog"
      actions={[{ label: 'Zum Blog', href: '/blog' }]}
    >
      {renderContent()}
    </DashboardListCard>
  );
};

// ============================================================================
// Termine-Karte
// ============================================================================

const AppointmentsCard: React.FC<{
  appointments: CardDataState<Appointment[]>;
  onRetry: () => void;
}> = ({ appointments, onRetry }) => {
  const renderContent = () => {
    if (appointments.loading) return <CardSkeleton />;
    if (appointments.error) return <CardError message={appointments.error} onRetry={onRetry} />;

    const upcoming = (appointments.data || [])
      .filter((a) => !a.is_reservation && a.status !== 'cancelled')
      .sort((a, b) => {
        const dateA = new Date(`${a.scheduled_date}T${a.scheduled_start_time}`);
        const dateB = new Date(`${b.scheduled_date}T${b.scheduled_start_time}`);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 3);

    if (upcoming.length === 0) {
      return (
        <CardEmpty
          icon={<CalendarClock className="w-8 h-8" />}
          message="Keine anstehenden Termine"
          actionLabel="Termin buchen"
          actionHref="/termine"
        />
      );
    }

    return (
      <div className="space-y-3">
        {upcoming.map((apt) => (
          <div
            key={apt.id}
            className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5"
          >
            {/* Datum-Block */}
            <div className="text-center min-w-[4rem] flex-shrink-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {formatShortDate(apt.scheduled_date)}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatTime(apt.scheduled_start_time)}
              </p>
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {apt.type_name}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                {/* Modus */}
                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  {apt.appointment_mode === 'online'
                    ? <><Video className="w-3 h-3" /> Online</>
                    : <><MapPin className="w-3 h-3" /> Vor Ort</>
                  }
                </span>
                {/* Status */}
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getAppointmentStatusClasses(apt.status)}`}>
                  {getAppointmentStatusLabel(apt.status)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const hasAppointments = !appointments.loading && !appointments.error &&
    (appointments.data || []).filter((a) => !a.is_reservation && a.status !== 'cancelled').length > 0;

  return (
    <DashboardListCard
      icon={<CalendarClock className="w-5 h-5" />}
      iconColor="teal"
      label="Termine"
      actions={
        !appointments.loading && !appointments.error
          ? hasAppointments
            ? [
                { label: 'Termin buchen', href: '/termine' },
                { label: 'Alle Termine', href: '/termine?tab=meine-termine' },
              ]
            : undefined // CardEmpty hat eigenen Link
          : undefined
      }
    >
      {renderContent()}
    </DashboardListCard>
  );
};

// ============================================================================
// Rechnungen-Karte
// ============================================================================

const InvoicesCard: React.FC<{
  invoices: CardDataState<Invoice[]>;
  onRetry: () => void;
}> = ({ invoices, onRetry }) => {
  const handleInvoiceClick = (inv: Invoice) => {
    const pdfUrl = getInvoicePdfUrl(inv.pdf_url);
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const renderContent = () => {
    if (invoices.loading) return <CardSkeleton />;
    if (invoices.error) return <CardError message={invoices.error} onRetry={onRetry} />;

    const recent = (invoices.data || []).slice(0, 5);

    if (recent.length === 0) {
      return (
        <CardEmpty
          icon={<Receipt className="w-8 h-8" />}
          message="Noch keine Rechnungen vorhanden"
        />
      );
    }

    return (
      <div className="space-y-2.5">
        {recent.map((inv) => {
          const isRefund = inv.total < 0;
          return (
            <button
              key={inv.id}
              onClick={() => handleInvoiceClick(inv)}
              className="w-full flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors cursor-pointer text-left"
            >
              {/* Links: Nummer + Datum + Typ */}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {inv.invoice_number}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFullDate(inv.issue_date)}
                  {isRefund && (
                    <span className="ml-1.5 text-orange-500 dark:text-orange-400 font-medium">
                      Rückerstattung
                    </span>
                  )}
                </p>
              </div>

              {/* Rechts: Betrag + Status */}
              <div className="text-right flex-shrink-0 ml-3">
                <p className={`text-sm font-semibold ${isRefund ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {formatAmount(Math.abs(inv.total), inv.currency)}
                </p>
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${getPaymentStatusClasses(inv.payment_status)}`}>
                  <PaymentStatusIcon paymentStatus={inv.payment_status} />
                  {getPaymentStatusLabel(inv.payment_status)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const hasInvoices = !invoices.loading && !invoices.error && (invoices.data || []).length > 0;

  return (
    <DashboardListCard
      icon={<Receipt className="w-5 h-5" />}
      iconColor="green"
      label="Rechnungen"
      actions={
        hasInvoices
          ? [{ label: 'Alle Rechnungen', href: '/profil/rechnungen' }]
          : undefined
      }
    >
      {renderContent()}
    </DashboardListCard>
  );
};

// ============================================================================
// Hauptseite
// ============================================================================

export const DashboardPageTW: React.FC = () => {
  const { user } = useAuth();
  const { appointments, invoices, subscription, unreadBlog, reload } = useDashboardData();
  const { hasAccess: hasTermineAccess, isLoading: termineLoading } = useFeatureAccess('termine_module');
  useLenis();

  if (!user) {
    return (
      <SidebarLayout showFooter>
        <TWContainer adaptive>
          <div className="text-center text-gray-600 dark:text-gray-400 py-12">
            Du musst angemeldet sein, um das Dashboard zu sehen.
          </div>
        </TWContainer>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout showFooter>
      {/* Header - IMMER volle Breite */}
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <TWPageHeader title="Dashboard" />
      </div>

      {/* Content - mit bedingter max-width auf Desktop (via TWContainer adaptive) */}
      <TWContainer adaptive spacing="default">
        <div>
          {/* Willkommens-Banner - volle Breite */}
          <div className="rounded-xl p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <UserAvatar user={user} size="md" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                    Willkommen zurück, {user.firstName}!
                  </h3>
                </div>
              </div>

              {/* Info-Badges - rechts auf Desktop, darunter auf Mobile */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500 dark:text-gray-400 sm:border-l sm:border-gray-100 sm:dark:border-white/5 sm:pl-5 border-t sm:border-t-0 border-gray-100 dark:border-white/5 pt-3 sm:pt-0">
                {/* Status */}
                {user.role === 'admin' ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-brand">
                    <Wrench className="w-3.5 h-3.5" />
                    Admin
                  </span>
                ) : user.isBetaTester ? (
                  <span className="inline-flex items-center gap-1.5 text-sky-700 dark:text-sky-400">
                    <FlaskConical className="w-3.5 h-3.5" />
                    Beta-Tester
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-lime-700 dark:text-lime-400">
                    <Crown className="w-3.5 h-3.5" />
                    Kunde
                  </span>
                )}

                {/* Abonnements */}
                {!subscription.loading && subscription.data && (() => {
                  const validSubs = subscription.data.subscriptions.filter(isSubscriptionValid);
                  if (validSubs.length === 0) return (
                    <span className="inline-flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5" />
                      Kein aktives Abo
                    </span>
                  );
                  return validSubs.map((sub: UserSubscription) => {
                    const isCancelled = isSubscriptionCancelled(sub) && sub.currentPeriodEnd;
                    const endDate = isCancelled
                      ? new Date(sub.currentPeriodEnd).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
                      : null;
                    return (
                      <span key={sub.id} className="inline-flex items-center gap-1.5">
                        <Crown className={`w-3.5 h-3.5 ${isCancelled ? 'text-status-error dark:text-red-400' : 'text-brand'}`} />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                          {getPlanDescription(sub.planSlug)}
                        </span>
                        {isCancelled ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium leading-none bg-status-error/10 text-red-600 dark:text-red-400">
                            Gekündigt - aktiv bis {endDate}
                          </span>
                        ) : (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium leading-none ${
                            sub.status === 'active'
                              ? 'bg-status-success/10 text-green-600 dark:text-green-400'
                              : 'bg-status-info/10 text-blue-600 dark:text-blue-400'
                          }`}>
                            {getStatusLabel(sub.status)}
                          </span>
                        )}
                      </span>
                    );
                  });
                })()}

                {/* Mitglied seit */}
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Mitglied seit {new Date(user.createdAt).toLocaleDateString('de-DE', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Dashboard Cards Grid - Neuigkeiten jetzt hier integriert */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:auto-rows-[440px] xl:auto-rows-[340px] gap-6">
            <AnnouncementsCard />
            <BlogCard
              unreadBlog={unreadBlog}
              onRetry={reload}
            />
            {!termineLoading && hasTermineAccess && (
              <AppointmentsCard appointments={appointments} onRetry={reload} />
            )}
            <InvoicesCard invoices={invoices} onRetry={reload} />
          </div>
        </div>
      </TWContainer>
    </SidebarLayout>
  );
};
